import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

import { RecentResource } from '@components/features/Overview/QuickLinks/types';
import { useLocalState, useLocalStateAction } from '@hooks/use-local-state';
import { useUserQuery } from '@queries/user';

function useRecentsKey() {
  const params = useParams();
  const { data: user } = useUserQuery();
  return `${params.teamUuid}-${params.appUuid}-${user?.id}-recents`;
}

export function useRecentActions() {
  const key = useRecentsKey();
  const setRecents = useLocalStateAction<RecentResource[]>(key, []);

  const add = useCallback(
    (recent: RecentResource) => {
      setRecents((prev) => [
        recent,
        ...(prev || []).filter(
          (r) => !(r.type === recent.type && r.id === recent.id)
        ),
      ]);
    },
    [setRecents]
  );

  const remove = useCallback(
    (recent: RecentResource) => {
      setRecents((prev) =>
        (prev || []).filter(
          (r) => !(r.type === recent.type && r.id === recent.id)
        )
      );
    },
    [setRecents]
  );

  const clearAll = useCallback(() => {
    setRecents([]);
  }, [setRecents]);

  return useMemo(() => ({ add, remove, clearAll }), [add, remove, clearAll]);
}

export function useRecents() {
  const key = useRecentsKey();
  const [data] = useLocalState<RecentResource[]>(key, []);
  return data;
}
